/**
 * Data transformation functions.
 *
 * Convert StationDataHandler / ChartsDataContainer data into
 * the flat ChartDataPoint[] format expected by ChartConfig.
 */
import {DataGranularity, HistoryMetric} from '../../api/types';
import * as Units from '../Units';
import ChartsDataContainer from '../NetatmoCommon/ChartsDataContainer';
import {getConversionMetric} from './formatters';
import {ChartDataPoint} from './types';

/**
 * Range metric pairs: when these are present together, they form
 * a [min, max] tuple stored under the range key.
 */
const RANGE_PAIRS: Record<string, {min: string; max: string}> = {
  T_RANGE: {min: 'T_MIN', max: 'T_MAX'},
  U_RANGE: {min: 'U_MIN', max: 'U_MAX'},
  SLP_RANGE: {min: 'SLP_MIN', max: 'SLP_MAX'},
  UV_RANGE: {min: 'UV_MIN', max: 'UV_MAX'},
  SOLAR_RADIATION_RANGE: {
    min: 'SOLAR_RADIATION_MIN',
    max: 'SOLAR_RADIATION_MAX',
  },
  VIS_RANGE: {min: 'VIS_MIN', max: 'VIS_MAX'},
  AQI_RANGE: {min: 'AQI_MIN', max: 'AQI_MAX'},
  LIGHTNING_STRIKE_DISTANCE_RANGE: {
    min: 'LIGHTNING_STRIKE_DISTANCE_MIN',
    max: 'LIGHTNING_STRIKE_DISTANCE_MAX',
  },
};

/**
 * Transforms time-series data from a ChartsDataContainer into the flat
 * ChartDataPoint[] format, applying unit conversions.
 *
 * @param handler    The data container (StationDataHandler or ChartsDataContainer)
 * @param elementId  Station ID (number) or module ID (string)
 * @param metrics    Array of metric keys to extract
 * @param granularity Data granularity
 * @param start      Start timestamp (unix seconds)
 * @param end        End timestamp (unix seconds)
 * @param units      User's preferred unit system
 * @returns Array of ChartDataPoint sorted by timestamp ascending
 */
export const transformHistoryData = (
  handler: ChartsDataContainer,
  elementId: number | string,
  metrics: HistoryMetric[],
  granularity: DataGranularity,
  start: number,
  end: number,
  units: Units.Units,
): ChartDataPoint[] => {
  const id = String(elementId);

  // Fetch raw data per metric: { [timestamp]: [value] } or { [timestamp]: [v1, v2, ...] }
  // When a single metric is passed, getChartData returns { [ts]: [value] }
  const dataByMetric: Record<string, Record<string, number[]>> = {};
  for (const metric of metrics) {
    dataByMetric[metric] = handler.getChartData(
      id,
      metric,
      granularity,
      start,
      end,
    );
  }

  // Collect all timestamps across all metrics
  const timestampSet = new Set<string>();
  for (const metric of metrics) {
    const data = dataByMetric[metric];
    if (data) {
      for (const ts of Object.keys(data)) {
        timestampSet.add(ts);
      }
    }
  }

  // Build flat array
  const points: ChartDataPoint[] = [];

  for (const tsStr of Array.from(timestampSet).sort()) {
    const ts = parseInt(tsStr, 10);
    const point: ChartDataPoint = {timestamp: ts};

    for (const metric of metrics) {
      const values = dataByMetric[metric]?.[tsStr];
      // getChartData wraps single metric in array: [value]
      const rawValue = values?.[0] ?? null;

      if (rawValue !== null && rawValue !== undefined) {
        const conversionMetric = getConversionMetric(metric);
        const converted = Units.convertValue(
          rawValue,
          conversionMetric,
          units,
        );
        point[metric] = converted;
      } else {
        point[metric] = null;
      }
    }

    // Build range tuples where both min and max are present
    for (const [rangeKey, {min, max}] of Object.entries(RANGE_PAIRS)) {
      const minVal = point[min];
      const maxVal = point[max];
      if (
        typeof minVal === 'number' &&
        typeof maxVal === 'number'
      ) {
        point[rangeKey] = [minVal, maxVal];
      }
    }

    points.push(point);
  }

  return points;
};

/**
 * Same as transformHistoryData but shifts all timestamps by `offset` seconds.
 * Used for comparison chart overlays (e.g. "same day last year" aligned to current period).
 */
export const transformComparisonData = (
  handler: ChartsDataContainer,
  elementId: number | string,
  metrics: HistoryMetric[],
  granularity: DataGranularity,
  start: number,
  end: number,
  offset: number,
  units: Units.Units,
): ChartDataPoint[] => {
  const data = transformHistoryData(
    handler,
    elementId,
    metrics,
    granularity,
    start,
    end,
    units,
  );
  return data.map((point) => ({
    ...point,
    timestamp: point.timestamp + offset,
  }));
};

/**
 * Merge comparison data into the main data array by matching timestamps.
 * Comparison metric values are stored under `{originalKey}_COMP` keys.
 *
 * @param mainData        Main period data
 * @param comparisonData  Comparison period data (timestamps already shifted)
 * @param metrics         Metric keys to merge
 * @returns Merged array with all timestamps from both datasets
 */
export const mergeComparisonIntoData = (
  mainData: ChartDataPoint[],
  comparisonData: ChartDataPoint[],
  metrics: string[],
): ChartDataPoint[] => {
  const mainMap = new Map<number, ChartDataPoint>();
  for (const point of mainData) {
    mainMap.set(point.timestamp, {...point});
  }

  for (const compPoint of comparisonData) {
    const existing = mainMap.get(compPoint.timestamp);
    if (existing) {
      // Merge comparison values into existing point
      for (const metric of metrics) {
        existing[`${metric}_COMP`] = compPoint[metric] ?? null;
      }
    } else {
      // Create new point with only comparison values
      const newPoint: ChartDataPoint = {timestamp: compPoint.timestamp};
      for (const metric of metrics) {
        newPoint[`${metric}_COMP`] = compPoint[metric] ?? null;
        newPoint[metric] = null; // main period has no data at this timestamp
      }
      mainMap.set(compPoint.timestamp, newPoint);
    }
  }

  return Array.from(mainMap.values()).sort(
    (a, b) => a.timestamp - b.timestamp,
  );
};

/**
 * Transform report data (from getOutdoorReport, getWindReport, etc.)
 * into ChartDataPoint[].
 *
 * Report data format is: { [formattedDate]: [value1, value2, ...] }
 * where values correspond to the metrics array in order.
 *
 * @param reportData  Report data object: { [dateStr]: number[] }
 * @param metrics     Ordered metric keys matching the values array
 * @param timezone    IANA timezone
 * @param units       User's preferred unit system
 * @returns Array of ChartDataPoint sorted by timestamp
 */
export const transformReportData = (
  reportData: Record<string, number[]>,
  metrics: string[],
  _timezone: string,
  units: Units.Units,
): ChartDataPoint[] => {
  const points: ChartDataPoint[] = [];

  for (const [dateStr, values] of Object.entries(reportData)) {
    // Parse YYYY/MM/DD date string back to timestamp
    const parts = dateStr.split('/');
    const year = parseInt(parts[0], 10);
    const month = parseInt(parts[1], 10) - 1; // 0-indexed
    const day = parseInt(parts[2], 10);
    const date = new Date(Date.UTC(year, month, day));
    const ts = Math.floor(date.getTime() / 1000);

    const point: ChartDataPoint = {timestamp: ts};

    for (let i = 0; i < metrics.length; i++) {
      const metric = metrics[i];
      const rawValue = values[i] ?? null;

      if (rawValue !== null && rawValue !== undefined) {
        const conversionMetric = getConversionMetric(metric as HistoryMetric);
        point[metric] = Units.convertValue(rawValue, conversionMetric, units);
      } else {
        point[metric] = null;
      }
    }

    // Build range tuples
    for (const [rangeKey, {min, max}] of Object.entries(RANGE_PAIRS)) {
      const minVal = point[min];
      const maxVal = point[max];
      if (typeof minVal === 'number' && typeof maxVal === 'number') {
        point[rangeKey] = [minVal, maxVal];
      }
    }

    points.push(point);
  }

  return points.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Transform monthly-aggregated report data (from convertOutdoorReportToAnnual etc.)
 * into ChartDataPoint[].
 *
 * Report data format is: { "YYYY/MM": [value1, value2, ...] }
 * where values correspond to the metrics array in order.
 *
 * Uses month index (0-11) as timestamp for category-based x-axis rendering,
 * and stores the category label in a `category` field.
 *
 * @param reportData     Monthly report data: { [monthStr]: number[] }
 * @param metrics        Ordered metric keys matching the values array
 * @param units          User's preferred unit system
 * @param categoryLabels Array of 12 category labels (e.g. short month names)
 * @returns Array of ChartDataPoint sorted by month index
 */
export const transformMonthlyReportData = (
  reportData: Record<string, number[]>,
  metrics: string[],
  units: Units.Units,
  categoryLabels?: string[],
): ChartDataPoint[] => {
  const points: ChartDataPoint[] = [];

  for (const [dateStr, values] of Object.entries(reportData)) {
    const parts = dateStr.split('/');
    const month = parseInt(parts[1], 10) - 1; // 0-indexed

    const point: ChartDataPoint = {
      timestamp: month,
      category: categoryLabels?.[month] ?? month.toString(),
    };

    for (let i = 0; i < metrics.length; i++) {
      const metric = metrics[i];
      const rawValue = values[i] ?? null;

      if (rawValue !== null && rawValue !== undefined) {
        const conversionMetric = getConversionMetric(metric as HistoryMetric);
        point[metric] = Units.convertValue(rawValue, conversionMetric, units);
      } else {
        point[metric] = null;
      }
    }

    // Build range tuples
    for (const [rangeKey, {min, max}] of Object.entries(RANGE_PAIRS)) {
      const minVal = point[min];
      const maxVal = point[max];
      if (typeof minVal === 'number' && typeof maxVal === 'number') {
        point[rangeKey] = [minVal, maxVal];
      }
    }

    points.push(point);
  }

  return points.sort((a, b) => a.timestamp - b.timestamp);
};

/**
 * Transform daily report data (from getMonthlyOutdoorReport etc.)
 * into ChartDataPoint[] with day-of-month categories.
 *
 * Report data format is: { "YYYY/MM/DD": [value1, value2, ...] }
 * where values correspond to the metrics array in order.
 *
 * Uses day index (0-based) as timestamp for category-based x-axis rendering,
 * and stores the day number string in a `category` field.
 *
 * When `daysInMonth` is provided, generates entries for ALL days of the month
 * (not just ones with data), so the x-axis shows the full month evenly spaced.
 *
 * @param reportData       Daily report data: { [dateStr]: number[] }
 * @param metrics          Ordered metric keys matching the values array
 * @param units            User's preferred unit system
 * @param options          Optional config for full-month rendering
 * @param options.daysInMonth  Total days in the month (generates empty entries for missing days)
 * @param options.tooltipLabels  Array of tooltip labels for each day (e.g. full formatted dates)
 * @returns Array of ChartDataPoint sorted by day index
 */
export const transformDailyReportData = (
  reportData: Record<string, number[]>,
  metrics: string[],
  units: Units.Units,
  options?: {
    daysInMonth?: number;
    tooltipLabels?: string[];
  },
): ChartDataPoint[] => {
  // Build a map from day number (1-based) to values
  const dataByDay: Record<number, number[]> = {};
  for (const [dateStr, values] of Object.entries(reportData)) {
    const parts = dateStr.split('/');
    const day = parseInt(parts[2], 10);
    dataByDay[day] = values;
  }

  const totalDays = options?.daysInMonth ?? Math.max(...Object.keys(dataByDay).map(Number), 0);
  const points: ChartDataPoint[] = [];

  for (let day = 1; day <= totalDays; day++) {
    const dayIndex = day - 1;
    const values = dataByDay[day];

    const point: ChartDataPoint = {
      timestamp: dayIndex,
      category: day.toString(),
    };

    if (options?.tooltipLabels?.[dayIndex]) {
      point.tooltipLabel = options.tooltipLabels[dayIndex];
    }

    if (values) {
      for (let i = 0; i < metrics.length; i++) {
        const metric = metrics[i];
        const rawValue = values[i] ?? null;

        if (rawValue !== null && rawValue !== undefined) {
          const conversionMetric = getConversionMetric(metric as HistoryMetric);
          point[metric] = Units.convertValue(rawValue, conversionMetric, units);
        } else {
          point[metric] = null;
        }
      }

      // Build range tuples
      for (const [rangeKey, {min, max}] of Object.entries(RANGE_PAIRS)) {
        const minVal = point[min];
        const maxVal = point[max];
        if (typeof minVal === 'number' && typeof maxVal === 'number') {
          point[rangeKey] = [minVal, maxVal];
        }
      }
    } else {
      // Explicitly set null for all metrics so Recharts doesn't treat
      // missing keys as 0 when computing axis domains
      for (const metric of metrics) {
        point[metric] = null;
      }
    }

    points.push(point);
  }

  return points;
};

/**
 * Returns a copy of `data` with a running-sum field added under
 * `cumulativeMetric`, computed from `sourceMetric`. Null/undefined source
 * values carry the previous cumulative forward without resetting it.
 *
 * Granularity-agnostic: the caller chooses the input granularity (hourly,
 * daily, monthly, ...) — the helper just sums what's there in order.
 */
export const computeCumulative = (
  data: ChartDataPoint[],
  sourceMetric: string,
  cumulativeMetric: string,
): ChartDataPoint[] => {
  let cumulative = 0;
  return data.map((point) => {
    const value = point[sourceMetric];
    if (typeof value === 'number') {
      cumulative += value;
    }
    return {...point, [cumulativeMetric]: cumulative};
  });
};
